import {
  HelperFunctions,
} from "@/scripts/Common/HelperFunctions";

import {
  isValidBIC,
  isValidIBAN,
} from "ibantools";

import type {
  AvailableCardRanges,
} from "@/components/cards/scripts";

const isRequired = (value: string | null | undefined) => !!value;

const isSmallerOrEqualToMaxLength = (value: string | null | undefined, length: number) => value != null && value.length <= length;

const isBiggerOrEqualToMinLength = (value: string | null | undefined, length: number) => value != null && value.length >= length;

const isSmallerOrEqualTo = (value: number, maxAmount: number) => value <= maxAmount;

const isBiggerOrEqualTo = (value: number, minAmount: number) => value >= minAmount;

const isValidEmail = (value: string) => HelperFunctions.IsEmailValid(value);

const hasValidDecimals = (value: number | undefined | null, maxDecimals: number) => value !== undefined
  && value !== null
  && HelperFunctions.GetDecimalsCount(value.toString()) <= maxDecimals;

const isStringAndHasValue = (value: any): value is string => typeof value === 'string' && value.length > 0;

const startDateBeforeEndDate = (startDate: string | undefined, endDate: string | undefined) => {
  if (!endDate) return true;
  if (!startDate) return false;

  return new Date(endDate).getTime() > new Date(startDate).getTime();
}

const isNumberInRange = (number: number, min: number, max: number) => number >= min && number <= max;

const isValidIban = (value: any) => typeof value === 'string' && isValidIBAN(value);

const isValidBic = (value: any) => typeof value === 'string' && isValidBIC(value)

const isWholeNumber = (value: any) => !Number.isNaN(Number.parseInt(value, 10)) && value % 1 === 0;

const isStartAndEndInRanges = (ranges: AvailableCardRanges[], startNumber: number, endNumber: number) => {
  if (!ranges) return true;

  return ranges.some((range) => range.from <= startNumber
    && range.from <= endNumber
    && range.to >= startNumber
    && range.to >= endNumber
  );
}

const isNumberInRanges = (ranges: AvailableCardRanges[], value: number) => {
  if (!ranges) return true;

  return ranges.some((range) => value >= range.from && value <= range.to);
};

const isEndEqualOrHigherThanStart = (start: number, end: number) => end >= start;

const isStringAndHasLength = (value: any, length:number) => typeof value === 'string' && value.length <= length;

const isNumberAndGreaterOrEqualAsMinValue = (value: number, minValue:number) => value >= minValue;

const isNumberAndSmallerOrEqualAsMaxValue = (value: number, maxValue:number) => value <= maxValue;

const hasCorrectDecimalsCount = (value: number, maxDecimals: number) => HelperFunctions.GetDecimalsCount(value.toString()) <= maxDecimals;

const isNotArrayOrEmpty = (value: any[]) => !Array.isArray(value) || value.length === 0;

const isNullOrUndefined = (value: any | undefined | null) => value === undefined || value === null;

const isNotNullish = <T>(value: T): value is NonNullable<T> => value !== null && value !== undefined;

const isEmpty = (value: string | number | null | undefined): boolean => {
  return value === null || value === undefined || (typeof value === 'string' && value === '');
};

export {
  isRequired,
  isSmallerOrEqualToMaxLength,
  isBiggerOrEqualToMinLength,
  isValidEmail,
  isSmallerOrEqualTo,
  isBiggerOrEqualTo,
  hasValidDecimals,
  startDateBeforeEndDate,
  isStringAndHasValue,
  isNumberInRange,
  isValidIban,
  isValidBic,
  isWholeNumber,
  isStartAndEndInRanges,
  isNumberInRanges,
  isEndEqualOrHigherThanStart,
  isStringAndHasLength,
  isNumberAndGreaterOrEqualAsMinValue,
  isNumberAndSmallerOrEqualAsMaxValue,
  hasCorrectDecimalsCount,
  isNotArrayOrEmpty,
  isNullOrUndefined,
  isNotNullish,
  isEmpty,
}
